import { yupResolver } from "@hookform/resolvers/yup";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  addOfficeLocation,
  findOfficeLocation,
} from "../../../core/data/redux/officeLocationSlice";
import { all_routes } from "../../router/all_routes";
// import MapWithPolygon from "./mapWithPolygonSelector"; // Leaflet version - commented out
import GoogleMapWithPolygon from "./mapWithGoogleMaps"; // Google Maps version

interface AddLocationProps { }

const schema = Yup.object().shape({
  name: Yup.string()
    .required("Office name is required")
    .max(100, "Office name must be at most 100 characters")
    .test(
      'no-html-js',
      'Invalid input.',
      function (value) {
        if (!value) return true;

        // Check for HTML tags (including script, img, div, span, etc.)
        const htmlTagPattern = /<\s*\/?[a-zA-Z][^>]*>/i;

        // Check for JavaScript keywords and patterns
        const jsPatterns = [
          /\bscript\b/i,
          /\balert\b/i,
          /\bconsole\b/i,
          /\bdocument\b/i,
          /\bwindow\b/i,
          /\beval\b/i,
          /\bfunction\b/i,
          /\bvar\b/i,
          /\blet\b/i,
          /\bconst\b/i,
          /javascript:/i,
          /vbscript:/i,
          /onload/i,
          /onclick/i,
          /onmouseover/i,
          /onerror/i,
        ];

        // Check for HTML tags
        if (htmlTagPattern.test(value)) {
          return false;
        }

        // Check for JavaScript patterns
        for (const pattern of jsPatterns) {
          if (pattern.test(value)) {
            return false;
          }
        }

        return true;
      }
    )
    .test(
      'no-special-chars',
      'Office name cannot contain special characters like @, #, $, %, ^, &, *, +, =, <, >, {, }, [, ], |, \\, :, ;, ", \', ?, /, ~, `',
      function (value) {
        if (!value) return true;
        // Allow letters, numbers, spaces, hyphens, underscores, parentheses, and periods
        // Reject @, #, $, %, ^, &, *, +, =, <, >, {, }, [, ], |, \, :, ;, ", ', ?, /, ~, `
        const invalidChars = /[@#$%^&*+=<>{}[\]|\\:;"'?/~`]/;
        return !invalidChars.test(value);
      }
    ),
  address: Yup.string()
    .required("Office address is required")
    .max(255, "Office address must be at most 255 characters"),
  phone: Yup.string()
    .max(10, "Phone number must be at most 10 digits")
    .matches(/^[0-9]*$/, "Phone number must be numeric"),

  // 👇 Optional email
  email: Yup.string()
    .email("Office email must be a valid email address"),
  coordinates: Yup.array()
    .test(
      'has-valid-polygon',
      'Map polygon boundary is required. Please draw the office boundary on the map.',
      function (value) {
        if (!value || !Array.isArray(value) || value.length === 0) return false;
        const firstPoly = value[0];
        return Array.isArray(firstPoly) && firstPoly.length >= 3;
      }
    ),
  lat: Yup.string(),
  long: Yup.string(),
});

const AddOfficeLocation = (props: AddLocationProps) => {
  const { id } = useParams();
  const navigation = useNavigate();
  const routes = all_routes;
  const dispatch = useDispatch();
  const officeLocationState = useSelector(
    (state: any) => state.officeLocationState
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      address: "",
      phone: "",
      email: "",
      coordinates: [],
      lat: "",
      long: "",
    },
  });

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);

  React.useEffect(() => {
    if (id) {
      dispatch(findOfficeLocation({ id: id }));
    }
  }, [id]);

  // Validates form first; on success opens confirmation modal
  const onSubmit = (data: any) => {
    if (id) {
      data["id"] = id;
    }
    setPendingFormData(data);
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = () => {
    if (pendingFormData) {
      dispatch(addOfficeLocation(pendingFormData));
      setShowConfirmModal(false);
      setPendingFormData(null);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
    setPendingFormData(null);
  };

  const getMapCoordinates = (coordinates: any, position: any) => {
    setValue("coordinates", coordinates, { shouldValidate: true });
    if (position && position[0] && position[1]) {
      setValue("lat", String(position[0]), { shouldValidate: true });
      setValue("long", String(position[1]), { shouldValidate: true });
    }
  };

  React.useEffect(() => {
    if (
      officeLocationState.loading === "fulfilled" &&
      officeLocationState.action === "add"
    ) {
      const message =
        officeLocationState?.data?.data?.message ||
        (id
          ? "Office location updated successfully"
          : "Office location created successfully");
      toast.success(message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setTimeout(() => {
        navigation(routes.officeLocation.list);
      }, 1500);
    } else if (
      officeLocationState.loading === "rejected" &&
      officeLocationState.action === "add"
    ) {
      const message =
        officeLocationState.error || "Failed to save office location";
      toast.error(message, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (
      officeLocationState.loading === "fulfilled" &&
      officeLocationState.action === "find"
    ) {
      const result = officeLocationState?.data || null;
      if (result) {
        setValue("name", result.name);
        setValue("address", result.address);
        setValue("phone", result.phone);
        setValue("email", result.email);
        setValue("coordinates", result.coordinates);
        setValue("lat", result.lat);
        setValue("long", result.long);
      }
    }
  }, [officeLocationState.loading]);

  return (
    <>
      <div>
        <div className="page-wrapper cardhead">
          <div className="content container-fluid">
            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Add Office Locations</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.adminDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Office Locations</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Add
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
            {/* /Page Header */}
            <div className="row">
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title">Add Office Location</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleSubmit(onSubmit)} className="vh-150">
                      <div className="mb-3 row">
                        <label className="col-form-label col-md-2 fw-semibold">
                          Office Name <span className="text-danger">*</span>
                        </label>
                        <div className="col-md-10">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter office name"
                            {...register("name")}
                          />
                          {errors.name && (
                            <small className="text-danger">
                              {errors.name.message}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="mb-3 row">
                        <label className="col-form-label col-md-2 fw-semibold">
                          Office address <span className="text-danger">*</span>
                        </label>
                        <div className="col-md-10">
                          <textarea
                            rows={5}
                            cols={5}
                            className="form-control"
                            placeholder="Enter office address"
                            defaultValue={""}
                            {...register("address")}
                          />
                          {errors.address && (
                            <small className="text-danger">
                              {errors.address.message}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="mb-3 mb-0 row">
                        <label className="col-form-label col-md-2 p-2 fw-semibold">
                          Locate Office <span className="text-muted fs-12 fw-normal">(Optional)</span>
                        </label>
                        <div className="col-md-10">
                          <GoogleMapWithPolygon
                            getMapCoordinates={getMapCoordinates}
                          />
                          {errors.coordinates && (
                            <small className="text-danger d-block mt-2 fw-medium fs-13">
                              {errors.coordinates.message as string}
                            </small>
                          )}
                          {/* Leaflet version - commented out
                          <MapWithPolygon
                            getMapCoordinates={getMapCoordinates}
                          />
                          */}
                        </div>
                      </div>
                      {/* <div className="mb-3 row">
                        <label className="col-form-label col-md-2">
                          Office Phone
                        </label>
                        <div className="col-md-10">
                          <input
                            type="text"
                            className="form-control"
                            {...register("phone")}
                          />
                          {errors.phone && (
                            <small className="text-danger">
                              {errors.phone.message}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="mb-3 row">
                        <label className="col-form-label col-md-2">
                          Office Email
                        </label>
                        <div className="col-md-10">
                          <input
                            type="text"
                            className="form-control"
                            {...register("email")}
                          />
                          {errors.email && (
                            <small className="text-danger">
                              {errors.email.message}
                            </small>
                          )}
                        </div>
                      </div> */}



                      <div className="mb-1 row">
                        <div className="col-form-label col-md-8"></div>
                        <div className="col-md-2">
                          <Link
                            to={routes.officeLocation.list}
                            className="btn btn-outline-danger w-100"
                          >
                            Cancel
                          </Link>
                        </div>
                        <div className="col-md-2">
                          <button
                            type="submit"
                            className="btn btn-primary w-100"
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal on Submit */}
      {showConfirmModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1055,
          }}
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex={-1}
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-body text-center p-4">
                <div className="mb-3">
                  <span
                    className="avatar avatar-xl rounded-circle d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: 60,
                      height: 60,
                      backgroundColor: "#fff4e5",
                      color: "#ff9800",
                    }}
                  >
                    <i className="ti ti-map-pin-question fs-28" />
                  </span>
                </div>
                <h4 className="mb-2">Confirm {id ? "Update" : "Submission"}</h4>
                <p className="text-muted mb-4 fs-14">
                  {id
                    ? "Are you sure you want to update this office location and geo-fenced boundaries?"
                    : "Are you sure you want to save this new office location and geo-fenced boundaries?"}
                </p>
                <div className="d-flex justify-content-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4"
                    onClick={handleCancelSubmit}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={handleConfirmSubmit}
                  >
                    {id ? "Yes, Update" : "Yes, Submit"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddOfficeLocation;
